import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import JustificationModal from "../components/JustificationModal";
import {
  FaArrowLeft,
  FaDownload,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import "./GerenciarCandidatos.css";

const formatInscricaoId = (id) => id.toString().padStart(4, "0");

const getApiErrorMessage = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (typeof data === "string") {
    // Evita despejar HTML enorme no toast.
    const trimmed = data.trim();
    if (trimmed.startsWith("<!DOCTYPE") || trimmed.startsWith("<html")) {
      return fallbackMessage;
    }
    return trimmed || fallbackMessage;
  }

  const serverMessage = data?.message;
  const serverCode = data?.code;
  if (serverMessage && serverCode) {
    return `${serverMessage} (${serverCode})`;
  }
  if (serverMessage) {
    return serverMessage;
  }
  if (status) {
    return `${fallbackMessage} (HTTP ${status})`;
  }
  return fallbackMessage;
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "N/A";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
};

function GerenciarCandidatos() {
  const { edital_id } = useParams();
  const navigate = useNavigate();
  const [inscritos, setInscritos] = useState([]);
  const [pontuacaoLiberada, setPontuacaoLiberada] = useState(false);
  const [updatingPontuacaoLiberada, setUpdatingPontuacaoLiberada] = useState(false);
  const [pontuacoes, setPontuacoes] = useState({});
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [cargoFiltro, setCargoFiltro] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [savingId, setSavingId] = useState(null);
  const [invalidacaoModalOpen, setInvalidacaoModalOpen] = useState(false);
  const [invalidacaoTarget, setInvalidacaoTarget] = useState(null);
  const [isRevalidarFlow, setIsRevalidarFlow] = useState(false);
  const { addToast } = useContext(ToastContext);

  const rowClass = (inscrito) => {
    if (inscrito.invalidada) return "gc-row--invalid";
    const hasScore =
      inscrito.pontuacao !== null &&
      inscrito.pontuacao !== undefined &&
      String(inscrito.pontuacao) !== "";
    return hasScore ? "gc-row--scored" : "gc-row--pending";
  };

  const openInvalidar = (inscrito) => {
    setInvalidacaoTarget(inscrito);
    setIsRevalidarFlow(false);
    setInvalidacaoModalOpen(true);
  };

  const openRevalidar = (inscrito) => {
    setInvalidacaoTarget(inscrito);
    setIsRevalidarFlow(true);
    setInvalidacaoModalOpen(true);
  };

  const closeInvalidacaoModal = () => {
    setInvalidacaoModalOpen(false);
    setInvalidacaoTarget(null);
    setIsRevalidarFlow(false);
  };

  const submitInvalidacao = async (justificativaText) => {
    if (!invalidacaoTarget) return;
    try {
      await api.put(`/inscricoes/${invalidacaoTarget.id}/invalidacao`, {
        invalidada: !isRevalidarFlow,
        justificativa: isRevalidarFlow ? null : justificativaText,
      });
      addToast(
        isRevalidarFlow ? "Inscrição revalidada." : "Inscrição invalidada.",
        "success"
      );
      closeInvalidacaoModal();
      fetchInscritos();
    } catch (error) {
      // Ajuda a diagnosticar 500 em ambiente remoto.
      console.error("Erro ao atualizar invalidação da inscrição", {
        status: error?.response?.status,
        data: error?.response?.data,
        dataString:
          typeof error?.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error?.response?.data ?? null),
        headers: error?.response?.headers,
        message: error?.message,
      });
      addToast(
        getApiErrorMessage(error, "Erro ao atualizar invalidação da inscrição."),
        "error"
      );
    }
  };

  const cargoOptions = React.useMemo(() => {
    const set = new Set(
      (inscritos || []).map((i) => i.nome_cargo).filter(Boolean)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inscritos]);

  const summary = React.useMemo(() => {
    const total = inscritos.length;
    const comPontuacao = inscritos.filter(
      (i) => i.pontuacao !== null && i.pontuacao !== undefined && i.pontuacao !== ""
    ).length;
    const pcd = inscritos.filter((i) => i.possui_deficiencia === true).length;
    return { total, comPontuacao, pcd };
  }, [inscritos]);

  const sortedAndFilteredInscritos = React.useMemo(() => {
    let sortableItems = [...inscritos];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const normalizeValue = (value, key) => {
          if (key === "possui_deficiencia") {
            if (value === null || value === undefined) {
              return "nao informado";
            }
            return value ? "sim" : "nao";
          }
          if (key === "raca") {
            if (!value || value === "NAO_INFORMADO") {
              return "nao informada";
            }
            if (value === "Nao declarada") {
              return "prefiro nao declarar";
            }
            return value.toLowerCase();
          }
          if (typeof value === "string") {
            return value.toLowerCase();
          }
          return value;
        };

        const aValue = normalizeValue(a[sortConfig.key], sortConfig.key);
        const bValue = normalizeValue(b[sortConfig.key], sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems.filter((inscrito) => {
      if (cargoFiltro && inscrito.nome_cargo !== cargoFiltro) {
        return false;
      }

      const termo = termoPesquisa.toLowerCase();
      const pcdLabel =
        inscrito.possui_deficiencia === null ||
        inscrito.possui_deficiencia === undefined
          ? "nao informado"
          : inscrito.possui_deficiencia
          ? "sim"
          : "nao";
      const raceLabel = (() => {
        if (!inscrito.raca || inscrito.raca === "NAO_INFORMADO") {
          return "nao informada";
        }
        if (inscrito.raca === "Nao declarada") {
          return "prefiro nao declarar";
        }
        return inscrito.raca.toLowerCase();
      })();
      return (
        inscrito.nome_completo.toLowerCase().includes(termo) ||
        inscrito.nome_cargo.toLowerCase().includes(termo) ||
        inscrito.id.toString().includes(termo) ||
        inscrito.cpf.toLowerCase().includes(termo) ||
        pcdLabel.includes(termo) ||
        raceLabel.includes(termo)
      );
    });
  }, [inscritos, termoPesquisa, sortConfig, cargoFiltro]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort />;
    }
    if (sortConfig.direction === "asc") {
      return <FaSortUp />;
    }
    return <FaSortDown />;
  };

  const fetchInscritos = useCallback(async () => {
    try {
      const [{ data }, { data: edital }] = await Promise.all([
        api.get(`/inscricoes/edital/${edital_id}/inscritos`),
        api.get(`/editais/${edital_id}`),
      ]);
      setInscritos(data);
      setPontuacaoLiberada(Boolean(edital?.pontuacao_liberada));

      const nextPontuacoes = {};
      (data || []).forEach((inscrito) => {
        nextPontuacoes[inscrito.id] =
          inscrito.pontuacao === null || inscrito.pontuacao === undefined
            ? ""
            : String(inscrito.pontuacao);
      });
      setPontuacoes(nextPontuacoes);
    } catch (error) {
      addToast("Erro ao buscar inscritos.", "error");
    }
  }, [edital_id, addToast]);

  const handleTogglePontuacaoLiberada = async () => {
    try {
      setUpdatingPontuacaoLiberada(true);
      const novoValor = !pontuacaoLiberada;
      const { data } = await api.patch(`/editais/${edital_id}/pontuacao-liberada`, {
        pontuacao_liberada: novoValor,
      });
      setPontuacaoLiberada(Boolean(data?.pontuacao_liberada));
      addToast(
        novoValor
          ? "Pontuações liberadas para os candidatos."
          : "Pontuações ocultadas para os candidatos.",
        "success"
      );
    } catch (error) {
      addToast(
        getApiErrorMessage(error, "Erro ao atualizar visibilidade das pontuações."),
        "error"
      );
    } finally {
      setUpdatingPontuacaoLiberada(false);
    }
  };

  useEffect(() => {
    fetchInscritos();
  }, [fetchInscritos]);

  const handlePontuacaoChange = (inscricaoId, value) => {
    setPontuacoes((prev) => ({ ...prev, [inscricaoId]: value }));
  };

  const handleSalvarPontuacao = async (inscricaoId) => {
    const pontuacao = pontuacoes[inscricaoId];

    const pontuacaoTrim = String(pontuacao ?? "").trim();
    if (!pontuacaoTrim) {
      addToast("Por favor, insira uma pontuação.", "error");
      return;
    }
    const pontuacaoNumber = Number(pontuacaoTrim);
    if (Number.isNaN(pontuacaoNumber)) {
      addToast("Pontuação inválida.", "error");
      return;
    }

    try {
      setSavingId(inscricaoId);
      await api.put(`/inscricoes/${inscricaoId}/pontuacao`, {
        pontuacao: pontuacaoNumber,
      });
      fetchInscritos();
      addToast("Pontuação atribuída com sucesso!", "success");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Erro ao atribuir pontuação.",
        "error"
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleGerarRelatorio = async () => {
    try {
      const response = await api.get(`/relatorios/edital/${edital_id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_edital_${edital_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast("Erro ao gerar relatório.", "error");
    }
  };

  const handleDownloadDocumentos = async (inscricaoId, nomeCandidato) => {
    try {
      const response = await api.get(`/inscricoes/download/${inscricaoId}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `documentos_${nomeCandidato.replace(/\s+/g, "_")}.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      addToast("Erro ao baixar documentos.", "error");
    }
  };

  return (
    <div className="gc-page">
      <PageHeader title="Gerenciar Candidatos">
        <button
          onClick={handleTogglePontuacaoLiberada}
          className={pontuacaoLiberada ? "button-secondary" : "btn-modern btn-add"}
          disabled={updatingPontuacaoLiberada}
        >
          {updatingPontuacaoLiberada
            ? "Atualizando..."
            : pontuacaoLiberada
            ? "Ocultar Pontuações"
            : "Liberar Pontuações"}
        </button>
        <button onClick={handleGerarRelatorio}>
          Gerar Relatório PDF
        </button>
        <button onClick={() => navigate(-1)} className="button-secondary">
          <FaArrowLeft />
          <span>Voltar</span>
        </button>
      </PageHeader>
      <Card>
        <div className="summary-pills">
          <span className="pill">Total: {summary.total}</span>
          <span className="pill">Com pontuação: {summary.comPontuacao}</span>
          <span className="pill">PCD: {summary.pcd}</span>
          <span className={`pill ${pontuacaoLiberada ? "" : "pill--muted"}`}>
            Visibilidade para candidatos: {pontuacaoLiberada ? "Liberada" : "Oculta"}
          </span>
        </div>
        <div className="table-toolbar">
          <div className="input-with-icon">
            <FaSearch />
            <input
              type="search"
              placeholder="Buscar por nome, cargo, CPF ou inscrição"
              value={termoPesquisa}
              onChange={(e) => setTermoPesquisa(e.target.value)}
            />
          </div>
          <select
            value={cargoFiltro}
            onChange={(e) => setCargoFiltro(e.target.value)}
            aria-label="Filtrar por cargo"
          >
            <option value="">Todos os cargos</option>
            {cargoOptions.map((cargo) => (
              <option key={cargo} value={cargo}>
                {cargo}
              </option>
            ))}
          </select>
          <span className="pill">
            Exibindo: {sortedAndFilteredInscritos.length}
          </span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Nº Inscrição</th>
              <th onClick={() => requestSort("nome_completo")}>
                Nome {getSortIcon("nome_completo")}
              </th>
              <th>CPF</th>
              <th onClick={() => requestSort("nome_cargo")}>
                Cargo {getSortIcon("nome_cargo")}
              </th>
              <th>Inscrito em</th>
              <th>Pontuação</th>
              <th onClick={() => requestSort("possui_deficiencia")}>
                PCD {getSortIcon("possui_deficiencia")}
              </th>
              <th onClick={() => requestSort("raca")}>
                Cor/Raça {getSortIcon("raca")}
              </th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredInscritos.map((inscrito) => (
              <tr key={inscrito.id} className={rowClass(inscrito)}>
                <td data-label="Nº Inscrição">{formatInscricaoId(inscrito.id)}</td>
                <td data-label="Nome">
                  <Link
                    to={`/candidato/${inscrito.usuario_id}`}
                    className="link-accent"
                  >
                    {inscrito.nome_completo}
                  </Link>
                  {inscrito.invalidada ? (
                    <span className="gc-badge gc-badge--invalid">
                      Invalidada
                    </span>
                  ) : null}
                </td>
                <td data-label="CPF">{inscrito.cpf}</td>
                <td data-label="Cargo">{inscrito.nome_cargo}</td>
                <td data-label="Inscrito em">
                  {formatDateTime(inscrito.data_inscricao)}
                </td>
                <td data-label="Pontuação">{inscrito.pontuacao || "N/A"}</td>
                <td data-label="PCD">
                  {inscrito.possui_deficiencia === null ||
                  inscrito.possui_deficiencia === undefined
                    ? "Não informado"
                    : inscrito.possui_deficiencia
                    ? "Sim"
                    : "Não"}
                </td>
                <td data-label="Cor/Raça">
                  {(() => {
                    if (!inscrito.raca || inscrito.raca === "NAO_INFORMADO") {
                      return "Não informada";
                    }
                    if (inscrito.raca === "Nao declarada") {
                      return "Prefiro não declarar";
                    }
                    return inscrito.raca;
                  })()}
                </td>
                <td data-label="Ações">
                  <div className="inline-actions">
                    <div className="score-fields">
                      <input
                        type="number"
                        placeholder="Pontos"
                        value={pontuacoes[inscrito.id] ?? ""}
                        onChange={(e) =>
                          handlePontuacaoChange(inscrito.id, e.target.value)
                        }
                      />
                    </div>
                    <button
                      onClick={() => handleSalvarPontuacao(inscrito.id)}
                      className="btn-modern btn-add"
                      disabled={savingId === inscrito.id}
                    >
                      <span>{savingId === inscrito.id ? "Salvando..." : "Salvar"}</span>
                    </button>
                    {inscrito.invalidada ? (
                      <button
                        onClick={() => openRevalidar(inscrito)}
                        className="btn-modern btn-ghost"
                        title="Revalidar inscrição"
                      >
                        <span>Revalidar</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => openInvalidar(inscrito)}
                        className="btn-modern"
                        style={{ background: "var(--cor-erro)" }}
                        title="Invalidar inscrição"
                      >
                        <span>Invalidar</span>
                      </button>
                    )}
                    <button
                      onClick={() =>
                        handleDownloadDocumentos(
                          inscrito.id,
                          inscrito.nome_completo
                        )
                      }
                      className="btn-modern btn-file"
                      title="Baixar documentos"
                    >
                      <FaDownload />
                      <span>Documentos</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <JustificationModal
        isOpen={invalidacaoModalOpen}
        onClose={closeInvalidacaoModal}
        title={
          isRevalidarFlow
            ? "Revalidar inscrição"
            : "Invalidar inscrição"
        }
        message={
          isRevalidarFlow
            ? "Confirme para revalidar esta inscrição."
            : "Informe uma justificativa (o candidato verá essa mensagem)."
        }
        confirmLabel={isRevalidarFlow ? "Revalidar" : "Invalidar"}
        requireText={!isRevalidarFlow}
        placeholder={
          isRevalidarFlow
            ? "(sem justificativa)"
            : "Ex.: Documentos inconsistentes / CPF inválido / etc."
        }
        defaultValue={
          isRevalidarFlow
            ? ""
            : invalidacaoTarget?.justificativa_invalidacao || ""
        }
        onConfirm={submitInvalidacao}
      />
    </div>
  );
}

export default GerenciarCandidatos;
